import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, X } from 'lucide-react'
import { api, extractApiError } from '../lib/api'

/**
 * Inline Accept / Decline buttons for `space.invited` AND
 * `list.invited` notifications.
 *
 * Notification.link encoding tells us which endpoint to call:
 *   - `/app/spaces/<id>`                  → Space invite
 *   - `/app/spaces/<id>/lists/<listId>`   → List invite (the listId
 *      after `/lists/` is the disambiguator)
 *
 * Parsing the ids out of `notification.link` avoids needing a new
 * structured `data` JSON field on the Notification model — keeps the
 * backend surface stable.
 *
 * After a successful accept/decline:
 *   - swaps the buttons for an inline "Accepted" / "Declined" status
 *     so the user sees confirmation instantly
 *   - invalidates the notifications + spaces + workspaces + lists
 *     query keys so the bell badge, sidebar tree and current list
 *     view all refresh
 *
 * Stops both default + propagation on button clicks because in the
 * bell the entire row is wrapped in a `<Link>`. Without these, a click
 * on Accept would also navigate the user away mid-mutation.
 */
export function SpaceInviteActions({ notification }) {
  // Parse the link. Try the more specific list pattern first so a
  // list invite doesn't get mistakenly routed to the space endpoint.
  const link = notification.link ?? ''
  const listMatch = link.match(/\/app\/spaces\/([^/?#]+)\/lists\/([^/?#]+)/)
  const spaceMatch = link.match(/\/app\/spaces\/([^/?#]+)/)

  let kind = null
  let respondEndpoint = null
  let statusEndpoint = null
  let resourceKey = null
  if (listMatch) {
    kind = 'list'
    respondEndpoint = `/lists/${listMatch[2]}/members/respond`
    statusEndpoint = `/lists/${listMatch[2]}/members/me`
    resourceKey = `list:${listMatch[2]}`
  } else if (spaceMatch) {
    kind = 'space'
    respondEndpoint = `/spaces/${spaceMatch[1]}/members/respond`
    statusEndpoint = `/spaces/${spaceMatch[1]}/members/me`
    resourceKey = `space:${spaceMatch[1]}`
  }

  const qc = useQueryClient()

  // Ask the server what the actor's current membership status is.
  // Without this, an already-accepted invite re-renders with Accept /
  // Decline buttons every time the notification list re-mounts —
  // because the local "resolved" state only survives the lifetime of
  // a single component instance, but the notification row keeps
  // appearing in the bell until the inviter (or a future cleanup
  // sweep) prunes it. The server is the source of truth: if status
  // is 'accepted' the invitation is done, swap in a status pill.
  const statusQuery = useQuery({
    queryKey: ['invite-status', resourceKey],
    queryFn: async () => {
      const res = await api.get(statusEndpoint)
      // Endpoint returns { status: 'pending' | 'accepted' | 'none' }.
      return res.data?.status ?? 'none'
    },
    // No retries on 404 — the resource might have been deleted, in
    // which case we just hide the actions cleanly.
    retry: false,
    enabled: Boolean(statusEndpoint),
    staleTime: 30_000,
  })

  // `resolved` short-circuits the swap immediately on click so the
  // user sees confirmation without waiting on the query refetch.
  // The serverStatus then overrides on next render with whatever the
  // server actually has — keeping the two in sync.
  const [resolved, setResolved] = useState(null)

  const respondMutation = useMutation({
    mutationFn: async (action) => {
      // The route handlers read `req.body?.action` (see
      // space-members.routes.js / list-members.routes.js) — sending
      // it as a query param leaves the backend with `action ===
      // undefined`. Pass it in the JSON body instead.
      const res = await api.post(respondEndpoint, { action })
      return res.data
    },
    onSuccess: async (_data, action) => {
      const verb = action === 'accept' ? 'accepted' : 'declined'
      toast.success(`Invitation ${verb}.`)
      setResolved(verb)

      // Mark THIS notification read on the server so the bell badge
      // drops and the next time it renders, the row reads as "done".
      // The action is implicit consumption — clicking Accept /
      // Decline is the same as clicking the row to dismiss.
      try {
        await api.post(`/notifications/${notification.id}/read`)
      } catch {
        // best-effort — failure here only means the badge stays one
        // count too high until next refetch, not a blocker.
      }

      void qc.invalidateQueries({ queryKey: ['notifications', 'me'] })
      void qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
      void qc.invalidateQueries({ queryKey: ['invite-status', resourceKey] })
      void qc.invalidateQueries({ queryKey: ['spaces'] })
      void qc.invalidateQueries({ queryKey: ['workspaces'] })
      if (kind === 'list') {
        void qc.invalidateQueries({ queryKey: ['lists'] })
        void qc.invalidateQueries({ queryKey: ['list-members'] })
      }
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  if (!respondEndpoint) return null

  // Render priority: local optimistic > server status > pending.
  const serverStatus = statusQuery.data
  const effective =
    resolved ??
    (serverStatus === 'accepted'
      ? 'accepted'
      : serverStatus === 'none' && !statusQuery.isLoading
        ? 'declined'
        : null)

  if (effective) {
    return (
      <p
        className={
          'text-xs font-medium ' +
          (effective === 'accepted'
            ? 'text-[color:var(--color-success)]'
            : 'text-[color:var(--color-ink-muted)]')
        }
      >
        Invitation {effective}.
      </p>
    )
  }

  // While we're still figuring out the server status, keep the
  // buttons out of view rather than letting them flash and then
  // disappear — minor flicker fix.
  if (statusQuery.isLoading) return null

  // Wrapping callback — both buttons share the same swallow-the-Link
  // pattern, so factor it out instead of duplicating on each onClick.
  function handle(action) {
    return (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (respondMutation.isPending) return
      respondMutation.mutate(action)
    }
  }

  const pending = respondMutation.isPending

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handle('accept')}
        disabled={pending}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-wait"
      >
        <Check size={12} />
        Accept
      </button>
      <button
        type="button"
        onClick={handle('decline')}
        disabled={pending}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-semibold border border-[color:var(--color-rule-strong)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-wait"
      >
        <X size={12} />
        Decline
      </button>
    </div>
  )
}
